import { type RefObject, useCallback, useEffect, useState } from "react";

export type FullscreenChannel = {
    /** Whether the call is filling the screen right now. */
    active: boolean;
    /**
     * Whether the browser itself is doing it. False means the room is filling
     * the page on its own — the same picture, with the browser's own bars
     * still on screen.
     */
    native: boolean;
    toggle: () => void;
};

/** The prefixed half of the API, as Safari still spells it. */
type WebkitElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
};
type WebkitDocument = Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
};

function fullscreenElement(): Element | null {
    const doc = document as WebkitDocument;
    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/**
 * Filling the screen with the call.
 *
 * The reason this exists is a phone. A patient joining from a handset gives up
 * a third of a small screen to the browser's own address bar and to the room's
 * header, and what is left is a picture of a face about the size of a stamp —
 * in a product whose entire content is a face.
 *
 * Two mechanisms, one control. Where the Fullscreen API works, the browser is
 * asked, which on Android also takes the address bar away. On an iPhone it
 * does not exist at all — Safari there offers fullscreen to `<video>` elements
 * only, and a LiveKit tile is a div — so the fallback is the room pinning
 * itself over the page. That loses the browser's bars but wins back the
 * header, the app chrome and the page's own padding, which on a 390px screen
 * is most of what was missing.
 *
 * `active` is read back from the document rather than assumed, because
 * fullscreen ends in ways this component never hears about: Escape, the
 * system gesture, a call coming in. Escape is handled by hand for the
 * fallback, where no browser is going to do it for us.
 */
export function useFullscreen(target: RefObject<HTMLElement | null>): FullscreenChannel {
    /** The browser is doing it. */
    const [native, setNative] = useState(false);
    /** We are doing it, because the browser would not. */
    const [pinned, setPinned] = useState(false);

    useEffect(() => {
        const sync = () => {
            const on = fullscreenElement() !== null;
            setNative(on);
            // Both at once would be the room pinned over a page that is
            // already the whole screen — harmless, but it would survive the
            // browser leaving fullscreen and strand the patient.
            if (on) setPinned(false);
        };

        document.addEventListener("fullscreenchange", sync);
        document.addEventListener("webkitfullscreenchange", sync);
        return () => {
            document.removeEventListener("fullscreenchange", sync);
            document.removeEventListener("webkitfullscreenchange", sync);
        };
    }, []);

    useEffect(() => {
        if (!pinned) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setPinned(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [pinned]);

    const toggle = useCallback(() => {
        const element = target.current as WebkitElement | null;

        if (fullscreenElement() !== null) {
            const doc = document as WebkitDocument;
            void (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
            return;
        }
        if (pinned) {
            setPinned(false);
            return;
        }
        if (!element) return;

        const request =
            element.requestFullscreen?.bind(element) ??
            element.webkitRequestFullscreen?.bind(element);

        if (!request) {
            setPinned(true);
            return;
        }

        // Rejected as well as missing: a browser can refuse the request — an
        // iframe without the permission, a gesture it did not count — and the
        // person pressed a button that has to do something either way.
        try {
            const result = request();
            if (result instanceof Promise) result.catch(() => setPinned(true));
        } catch {
            setPinned(true);
        }
    }, [pinned, target]);

    return { active: native || pinned, native, toggle };
}
